import React, { useState, useEffect, useCallback } from 'react';
import { Input, Icon, Tooltip, message, Alert, Tabs } from 'antd';
import Loader from '../../components/Loader';
import Button from '../../components/Button';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { fetchAPIKey, regenerateAPIKey } from '../../utils/services/authAPI';
import { FullPageLoaderContainer } from '../../artboard/styles';
import { Header, Main } from '../MailRoom/styles';
import { InputContainer, InputLabel } from '../UserSettingsPage/styles';
import { Container, KeyRotationCard } from './styles';
import { withTranslation } from 'react-i18next';
import { INTEGRATION_KEY as trans } from './TranslationValues';
const NAMESPACES = ['INTEGRATION_KEY'];

const { TabPane } = Tabs;

const IntegrationKey = props => {
  const [apiKey, setApiKey] = useState(null);
  const [embedAuthenticationKey, setEmbedAuthenticationKey] = useState(null);
  const [apiKeyConfirmationModal, setApiKeyConfirmationModal] = useState(false);
  const [embedConfirmationModal, setEmbedConfirmationModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [activeKey, setActiveKey] = useState("1");
  const [isKeyVisible, setIsKeyVisible] = useState(false);
  const [isEmbedKeyVisible, setIsEmbedKeyVisible] = useState(false);
  const [rotationDueAt, setRotationDueAt] = useState(null);
  const [createdOn, setCreatedOn] = useState(null);
  const { t } = props;

  // Helper function to calculate days remaining until rotation
  const calculateDaysRemaining = (dueDate) => {
    if (!dueDate) return null;
    const now = new Date();
    const due = new Date(dueDate);
    const diffTime = due - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  // Helper function to format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Helper function to calculate rotation period in days
  const calculateRotationPeriod = (created, due) => {
    if (!created || !due) return null;
    const createdDate = new Date(created);
    const dueDate = new Date(due);
    const diffTime = dueDate - createdDate;
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  useEffect(() => {
    // The API key is retrieved.
    retrieveAPIKey();
    retrieveAPIKey('client_id');
  }, []);

  const retrieveAPIKey = async (keyType = '') => {
    setIsLoading(true);
    const response = keyType ? await fetchAPIKey({ type: keyType }) : await fetchAPIKey();
    if (response && response.statusCode === 1915 && response.errors.length === 0) {
      if (keyType) {
        setEmbedAuthenticationKey(response.data.apiKey);
      } else {
        setApiKey(response.data.apiKey);
        setRotationDueAt(response.data.rotationDueAt);
        setCreatedOn(response.data.createdOn);
      }
    }
    setIsLoading(false);
  };

  const regenerateKey = async (keyType = '') => {
    setIsRegenerating(true);
    const response = keyType ? await regenerateAPIKey({ type: keyType }) : await regenerateAPIKey()
    if (response && response.statusCode === 1917 && response.errors.length === 0) {
      message.success(t(trans.REGENERATED_SUCCESS_MESSAGE.key, trans.REGENERATED_SUCCESS_MESSAGE.value),);
      if (keyType) {
        setEmbedConfirmationModal(false)
        retrieveAPIKey(keyType)
      }
      else {
        setApiKeyConfirmationModal(false);
        retrieveAPIKey()
      }
    }
    setIsRegenerating(false);
  }

  const copyKey = useCallback((key) => {
    navigator.clipboard.writeText(key);
    message.success(t(trans.COPIED_SUCCESS_MESSAGE.key, trans.COPIED_SUCCESS_MESSAGE.value));
  }, [])

  return (
    <Main>
      <Container>
        <Header height={'20vh'}>
          <h2 className="title">
            {t(trans.ENTERPRISE_INTEGRATION_KEY_TITLE.key, trans.ENTERPRISE_INTEGRATION_KEY_TITLE.value)}
          </h2>
        </Header>

        {/* A loading indicator will be shown while the API is being processed; otherwise, the component will be rendered. */}
        {(isLoading) ? (
          <FullPageLoaderContainer>
            <Loader text={t(trans.LOADING_TEXT.key, trans.LOADING_TEXT.value) + '...'} />
          </FullPageLoaderContainer>
        ) : (

          <Tabs activeKey={activeKey} onChange={(key) => setActiveKey(key)}>
            <TabPane tab="API Integration key" key="1">
              <InputContainer className="input-container" style={{ marginTop: '50px' }} >
                <Alert
                  description={t(
                    trans.REGENERATION_WARNING_MESSAGE.key,
                    trans.REGENERATION_WARNING_MESSAGE.value,
                  )}
                  type="warning"
                  showIcon
                />
                <KeyRotationCard>
                  <div className="rotation-info">
                    <div className="rotation-title">{t(trans.KEY_ROTATION_TITLE.key, trans.KEY_ROTATION_TITLE.value)}</div>
                    <div className="rotation-details">{t(trans.KEY_ROTATION_DETAILS.key, trans.KEY_ROTATION_DETAILS.value)} {calculateRotationPeriod(createdOn, rotationDueAt)} {t(trans.KEY_ROTATION_DAYS.key, trans.KEY_ROTATION_DAYS.value)}</div>
                    <div className="rotation-expiry">
                      {t(trans.KEY_EXPIRES_ON.key, trans.KEY_EXPIRES_ON.value)} <span style={{ fontWeight: 600 }}>{formatDate(rotationDueAt)}</span>
                    </div>
                  </div>
                  <div className="days-remaining">
                    <span className="days-count">{calculateDaysRemaining(rotationDueAt)}</span>
                    <span className="days-label">{t(trans.DAYS_REMAINING.key, trans.DAYS_REMAINING.value)}</span>
                  </div>
                </KeyRotationCard>
                <Input
                  name="licenseKey"
                  size="large"
                  readOnly={true}
                  type={isKeyVisible ? "text" : "password"}
                  value={apiKey}
                  addonAfter={
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                      <Tooltip title={isKeyVisible ? t(trans.HIDE_KEY_TOOLTIP.key, trans.HIDE_KEY_TOOLTIP.value) : t(trans.SHOW_KEY_TOOLTIP.key, trans.SHOW_KEY_TOOLTIP.value)} trigger="hover">
                        <Icon 
                          type={isKeyVisible ? "eye" : "eye-invisible"} 
                          onClick={() => setIsKeyVisible(!isKeyVisible)}
                          style={{ cursor: 'pointer' }}
                        />
                      </Tooltip>
                      <Tooltip
                        title={t(trans.COPY_CLIPBOARD_TOOLTIP.key, trans.COPY_CLIPBOARD_TOOLTIP.value)}
                        trigger="hover"
                      >
                        <div className="copy-button" onClick={() => copyKey(apiKey)}>
                          <Icon type={'copy'} />{' '}
                          <p>{t(trans.COPY_BUTTON.key, trans.COPY_BUTTON.value)}</p>
                        </div>
                      </Tooltip>
                    </div>
                  }
                />
                <Button
                  className="custom-button"
                  label={t(trans.REGENERATE_BUTTON.key, trans.REGENERATE_BUTTON.value)}
                  large={true}
                  onClick={() => setApiKeyConfirmationModal(true)}
                />

                {/* The confirmation of API Key regeneration is accomplished through the use of this modal. */}
                <ConfirmationModal
                  showImage={false}
                  visibility={apiKeyConfirmationModal}
                  onCancel={() => setApiKeyConfirmationModal(false)}
                  onConfirm={() => regenerateKey()}
                  confirmationText={t(
                    trans.REGENERATE_CONFIRMATION_MESSAGE.key,
                    trans.REGENERATE_CONFIRMATION_MESSAGE.value,
                  )}
                  confirmButtonText={
                    isRegenerating
                      ? t(trans.REGENERATING_BUTTON.key, trans.REGENERATING_BUTTON.value)
                      : t(trans.PROCEED_BUTTON.key, trans.PROCEED_BUTTON.value)
                  }
                  isLoading={isRegenerating}
                />
              </InputContainer>
            </TabPane>
            <TabPane tab="Embed Integration Key" key="2">
              <InputContainer className="input-container" style={{ marginTop: '50px' }}>
                <Alert
                  description={t(trans.EMBED_WARNING_MESSAGE.key, trans.EMBED_WARNING_MESSAGE.value)}
                  type="warning"
                  showIcon
                />
                <Input
                  name="licenseKey"
                  size="large"
                  readOnly={true}
                  type={isEmbedKeyVisible ? "text" : "password"}
                  value={embedAuthenticationKey}
                  addonAfter={
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                      <Tooltip title={isEmbedKeyVisible ? t(trans.HIDE_KEY_TOOLTIP.key, trans.HIDE_KEY_TOOLTIP.value) : t(trans.SHOW_KEY_TOOLTIP.key, trans.SHOW_KEY_TOOLTIP.value)} trigger="hover">
                        <Icon 
                          type={isEmbedKeyVisible ? "eye" : "eye-invisible"} 
                          onClick={() => setIsEmbedKeyVisible(!isEmbedKeyVisible)}
                          style={{ cursor: 'pointer' }}
                        />
                      </Tooltip>
                      <Tooltip
                        title={t(trans.COPY_CLIPBOARD_TOOLTIP.key, trans.COPY_CLIPBOARD_TOOLTIP.value)}
                        trigger="hover"
                      >
                        <div className="copy-button" onClick={() => copyKey(embedAuthenticationKey)}>
                          <Icon type={'copy'} />{' '}
                          <p>{t(trans.COPY_BUTTON.key, trans.COPY_BUTTON.value)}</p>
                        </div>
                      </Tooltip>
                    </div>
                  }
                />
                <Button
                  className="custom-button"
                  label={'Regenerate Token'}
                  large={true}
                  onClick={() => setEmbedConfirmationModal(true)}
                />

                {/* The confirmation of API Key regeneration is accomplished through the use of this modal. */}
                <ConfirmationModal
                  showImage={false}
                  visibility={embedConfirmationModal}
                  onCancel={() => setEmbedConfirmationModal(false)}
                  onConfirm={() => regenerateKey('client_id')}
                  confirmationText={t(
                    trans.REGENERATE_CONFIRMATION_MESSAGE.key,
                    trans.REGENERATE_CONFIRMATION_MESSAGE.value,
                  )}
                  confirmButtonText={
                    isRegenerating
                      ? t(trans.REGENERATING_BUTTON.key, trans.REGENERATING_BUTTON.value)
                      : t(trans.PROCEED_BUTTON.key, trans.PROCEED_BUTTON.value)
                  }
                  isLoading={isRegenerating}
                />
              </InputContainer>
            </TabPane>
          </Tabs>
        )}
      </Container>
    </Main>
  );
};

export default withTranslation(NAMESPACES)(IntegrationKey);
